import { baseApi } from '@/app/baseApi';

import type {
  CreateScenarioRequest,
  CreateWizardRequest,
  SaveWizardDraftRequest,
  Scenario,
  UpdateWizardRequest,
  Wizard,
  WizardConfig,
} from '../types/wizard';

export const wizardsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWizards: builder.query<Wizard[], string>({
      query: (appId) => `/apps/${appId}/wizards`,
      providesTags: ['Wizard'],
    }),

    getWizard: builder.query<Wizard, { appId: string; wizardId: string }>({
      query: ({ appId, wizardId }) => `/apps/${appId}/wizards/${wizardId}`,
      providesTags: (_result, _error, { wizardId }) => [{ type: 'Wizard', id: wizardId }],
    }),

    createWizard: builder.mutation<Wizard, CreateWizardRequest>({
      query: ({ appId, ...data }) => ({
        url: `/apps/${appId}/wizards`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Wizard'],
    }),

    updateWizard: builder.mutation<Wizard, UpdateWizardRequest>({
      query: ({ wizardId, ...data }) => ({
        url: `/wizards/${wizardId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { wizardId }) => [{ type: 'Wizard', id: wizardId }],
    }),

    saveWizardDraft: builder.mutation<Wizard, SaveWizardDraftRequest>({
      query: ({ appId, wizardId, ...data }) => ({
        url: wizardId ? `/wizards/${wizardId}/draft` : `/apps/${appId}/wizards/draft`,
        method: wizardId ? 'PUT' : 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { wizardId }) =>
        wizardId ? [{ type: 'Wizard', id: wizardId }] : ['Wizard'],
    }),

    deleteWizard: builder.mutation<void, { appId: string; wizardId: string }>({
      query: ({ appId, wizardId }) => ({
        url: `/apps/${appId}/wizards/${wizardId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Wizard'],
    }),

    getWizardTypes: builder.query<unknown[], void>({
      query: () => '/wizards/types',
    }),

    generateRecipeFromWizard: builder.mutation<
      { recipe: string },
      { wizardId: string; config: WizardConfig }
    >({
      query: ({ wizardId, config }) => ({
        url: `/wizards/${wizardId}/generate`,
        method: 'POST',
        body: config,
      }),
    }),

    getMetadata: builder.query<unknown, string>({
      query: (connectionKey) => ({
        url: `/metadata/${connectionKey}`,
        endpoint: 'test-conn',
      }),
    }),

    getScenarioList: builder.query<
      Scenario[],
      { tenantId: string; accountId: string; typeGuid: string }
    >({
      query: ({ tenantId, accountId, typeGuid }) =>
        `/tenants/${tenantId}/accounts/${accountId}/scenarios?typeGuid=${typeGuid}`,
      providesTags: ['Scenario'],
    }),

    createScenario: builder.mutation<Scenario, CreateScenarioRequest>({
      query: (data) => ({
        url: '/scenarios',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Scenario'],
    }),
  }),
});

export const {
  useGetWizardsQuery,
  useGetWizardQuery,
  useCreateWizardMutation,
  useUpdateWizardMutation,
  useSaveWizardDraftMutation,
  useDeleteWizardMutation,
  useGetWizardTypesQuery,
  useGenerateRecipeFromWizardMutation,
  useGetMetadataQuery,
  useGetScenarioListQuery,
  useCreateScenarioMutation,
} = wizardsApi;
